'use client';

import s from 'Bomdisoft/app/[lang]/support/hub/styles.module.css';
import { SLA_SEVERITIES, SUPPORT_EMAIL } from 'Bomdisoft/app/[lang]/support/sla-content';
import { LegalPageShell } from 'Bomdisoft/shared/legal-page-shell';
import legal from 'Bomdisoft/shared/legal-page-shell/styles.module.css';
import { productCatalog } from 'Bomdisoft/shared/products';
import {
  supportChannelsHeading,
  supportChatBody,
  supportChatHeading,
  supportCommitmentsBody,
  supportCommitmentsCta,
  supportCommitmentsHeading,
  supportEmailBody,
  supportEmailCta,
  supportEmailHeading,
  supportEyebrow,
  supportLede,
  supportProductOverview,
  supportProductSite,
  supportProductsBody,
  supportProductsHeading,
  supportTitle,
  supportTitleAccent,
} from 'Bomdisoft/translations';
import { LinkButton } from '@bomdisoft/components';
import { getLanguageFromLocale } from '@bomdisoft/translations';
import Image from 'next/image';
import { useParams } from 'next/navigation';

export const SupportHub = () => {
  const params = useParams();
  const langPrefix = typeof params.lang === 'string' ? params.lang : 'en';
  const language = getLanguageFromLocale(langPrefix);

  return (
    <LegalPageShell eyebrow={supportEyebrow[language]} lede={supportLede[language]} testId='support-page' title={supportTitle[language]} titleAccent={supportTitleAccent[language]}>
      <section className={legal.section}>
        <div className={legal.content}>
          <h2 className={legal.sectionHeading}>{supportChannelsHeading[language]}</h2>
          <div className={s.cards}>
            <article className={s.card} data-testid='support-card-email'>
              <h3 className={s.cardHeading}>{supportEmailHeading[language]}</h3>
              <p className={legal.body}>{supportEmailBody[language].replace('{email}', SUPPORT_EMAIL)}</p>
              <LinkButton data-prodantix='Email support' data-prodantix-action='contact_support' data-testid='support-email-cta' href={`mailto:${SUPPORT_EMAIL}`} variant='contained'>
                {supportEmailCta[language]}
              </LinkButton>
            </article>
            <article className={s.card} data-testid='support-card-chat'>
              <h3 className={s.cardHeading}>{supportChatHeading[language]}</h3>
              <p className={legal.body}>{supportChatBody[language]}</p>
            </article>
          </div>
        </div>
      </section>
      <section className={legal.sectionAlt}>
        <div className={legal.content}>
          <h2 className={legal.sectionHeading}>{supportProductsHeading[language]}</h2>
          <p className={legal.body}>{supportProductsBody[language]}</p>
          <div className={s.productGrid}>
            {productCatalog.map(product => (
              <article className={s.productTile} data-testid={`support-product-${product.slug}`} key={product.slug}>
                <div className={s.productHead}>
                  <Image alt='' height={28} src={product.logo} width={28} />
                  <h3 className={s.productName}>{product.name}</h3>
                </div>
                <p className={s.productSummary}>{product.summary[language]}</p>
                <div className={s.productLinks}>
                  <a data-prodantix={`Visit the ${product.name} website`} href={`https://${product.host}`}>
                    {supportProductSite[language]}
                  </a>
                  <a data-prodantix={`Read the ${product.name} overview`} href={`/${langPrefix}/products/${product.slug}`}>
                    {supportProductOverview[language]}
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className={legal.section}>
        <div className={legal.content}>
          <h2 className={legal.sectionHeading}>{supportCommitmentsHeading[language]}</h2>
          <p className={legal.body}>{supportCommitmentsBody[language]}</p>
          <ul className={legal.list}>
            {SLA_SEVERITIES.map(severity => (
              <li key={severity.level}>
                <span className={legal.strong}>{`${severity.name[language]}:`}</span> {severity.target[language]}
              </li>
            ))}
          </ul>
          <LinkButton data-prodantix='Read the support response times' data-testid='support-sla-cta' href={`/${langPrefix}/support/sla`}>
            {supportCommitmentsCta[language]}
          </LinkButton>
        </div>
      </section>
    </LegalPageShell>
  );
};
